import React from 'react';
import { motion } from 'framer-motion';
import { UserCheck, ShieldCheck, Coins } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ValuePropositions: React.FC = () => {
    const { t } = useTranslation('marketing');

    const propositions = [
        { icon: UserCheck, titleKey: 'value_propositions.human_in_command_title', descKey: 'value_propositions.human_in_command_desc' },
        { icon: ShieldCheck, titleKey: 'value_propositions.decision_guardian_title', descKey: 'value_propositions.decision_guardian_desc' },
        { icon: Coins, titleKey: 'value_propositions.earn_knowledge_title', descKey: 'value_propositions.earn_knowledge_desc' },
    ];

    return (
        <section className="relative py-24 md:py-32 overflow-hidden" id="value-propositions">
            {/* Static separator line */}
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-hig-accent/20 to-transparent" />

            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/20 to-transparent" />
            <div className="container-hig">
                {/* Section header */}
                <motion.div
                    className="text-center mb-14"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-white mb-4 tracking-tight">
                        {t('value_propositions.title')}
                    </h2>
                </motion.div>

                {/* Value proposition grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {propositions.map((prop, index) => {
                        const IconComponent = prop.icon;
                        return (
                            <motion.div
                                key={index}
                                className="relative group card-surface p-7 md:p-8 text-center md:text-left"
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-60px' }}
                                transition={{ duration: 0.5, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
                                whileHover={{ borderColor: 'rgba(10,132,255,0.15)', y: -4, transition: { duration: 0.3 } }}
                            >
                                {/* Hover glow */}
                                <div className="absolute -inset-1 rounded-2xl pointer-events-none blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                                    style={{ background: 'radial-gradient(circle at 50% 50%, rgba(10,132,255,0.06), transparent 70%)' }}
                                />

                                {/* Icon with glow pulse on scroll */}
                                <div className="relative w-10 h-10 mb-5 mx-auto md:mx-0">
                                    <motion.div
                                        className="absolute inset-0 rounded-xl blur-xl pointer-events-none"
                                        style={{ background: 'rgba(10,132,255,0.15)' }}
                                        initial={{ opacity: 0, scale: 0.5 }}
                                        whileInView={{
                                            opacity: [0, 0.8, 0],
                                            scale: [0.5, 1.5, 0.8],
                                        }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 2, delay: 0.5 + index * 0.2, ease: 'easeInOut' }}
                                    />
                                    <div className="relative w-10 h-10 icon-box">
                                        <IconComponent className="w-5 h-5 text-hig-accent/70" />
                                    </div>
                                </div>

                                {/* Title */}
                                <h3 className="relative text-base font-semibold text-white mb-3">
                                    {t(prop.titleKey)}
                                </h3>

                                {/* Description */}
                                <p className="relative text-sm text-white/40 leading-relaxed">
                                    {t(prop.descKey)}
                                </p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default ValuePropositions;
